import { type ReactNode } from 'react';

import { NoData, Stack } from '@cast/design-system';
import { useNavigate } from '@cast/infrastructure-organization';
import { useDoesReportingMonetizationApply } from '@cast/shared-trial-ui';

import { FailedToLoad } from '@cast/shared-messages-ui';

import { usePlatformUsageReportQuery } from 'hooks/queries/organization';

import { OverviewCard, OverviewCardSkeleton } from './_components/OverviewCard';
import { SubscriptionsBar } from './_components/SubscriptionsBar';
import { DATABASE_OPTIMIZATION_FEATURE_KEY } from './database-optimization/constants';
import { DatabaseOptimizationOverviewCard } from './database-optimization/_components/DatabaseOptimizationOverviewCard';
import { getUnitLabel, sortPlatformUsageFeatures } from './_utils';

type PlatformUsageProps = {
  /** Extra overview cards (e.g. Reporting monetization) rendered with billing features. */
  extraCards?: ReactNode;
};

export const PlatformUsage = ({ extraCards }: PlatformUsageProps) => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = usePlatformUsageReportQuery();
  // Hide Autoscaler/WOOP (etc.) billing cards for reporting-monetization orgs;
  // marketplace subscriptions bar still shows. Ineligible orgs keep classic cards.
  const hideClassicBillingCards = useDoesReportingMonetizationApply();

  const subscriptions = data?.subscriptions ?? [];
  const features = sortPlatformUsageFeatures(data?.features ?? []);
  const validFeatures = hideClassicBillingCards ? [] : features;
  const hasExtraCards = !!extraCards;
  const isEmpty = !isLoading && validFeatures.length === 0 && !hasExtraCards;

  if (isError) {
    return (
      <Stack gap={16}>
        <FailedToLoad />
        {hasExtraCards ? <Stack gap={24}>{extraCards}</Stack> : null}
      </Stack>
    );
  }

  return (
    <Stack gap={16}>
      <SubscriptionsBar subscriptions={subscriptions} isLoading={isLoading} />
      <Stack gap={24}>
        {isLoading ? (
          Array.from({ length: hideClassicBillingCards ? 1 : 3 }, (_, i) => (
            <OverviewCardSkeleton key={i} />
          ))
        ) : isEmpty ? (
          <NoData sx={{ mt: 64 }} entity="feature" />
        ) : (
          <>
            {validFeatures.map((feature) => {
              if (feature.feature === DATABASE_OPTIMIZATION_FEATURE_KEY) {
                return (
                  <DatabaseOptimizationOverviewCard
                    key={feature.feature}
                    feature={feature}
                    onClick={() => navigate(feature.feature!)}
                  />
                );
              }

              const freeOfCharge = feature.freeOfCharge;
              const credits = feature.credits;

              return (
                <OverviewCard
                  key={feature.feature}
                  testId="platform-usage-overview-card"
                  title={feature.displayName ?? feature.feature ?? ''}
                  badge={
                    freeOfCharge?.isFreeOfCharge
                      ? {
                          label: 'Free of charge',
                          endDate: freeOfCharge.endDate,
                        }
                      : undefined
                  }
                  usage={{
                    value: feature.usage ?? 0,
                    unit: getUnitLabel(feature.unit),
                  }}
                  credits={{
                    used: credits?.used ?? 0,
                    total: credits?.total ?? 0,
                    remaining: credits?.remaining ?? 0,
                    period: credits?.resetFrequency,
                    periodTooltip:
                      credits?.resetFrequency === 'monthly'
                        ? 'Your credit allocation renews on the 1st of each month.'
                        : undefined,
                  }}
                  onClick={() => navigate(feature.feature!)}
                />
              );
            })}
            {extraCards}
          </>
        )}
      </Stack>
    </Stack>
  );
};
